import { useBalance } from '../api'
import { BonusFunds, SpendableFunds } from './Funds'

/**
 * The hover panel behind the balance in the menu bar.
 *
 * It replaces a native `title` tooltip that read "Deposit/Withdraw" — a fixed string that said
 * nothing about the money it was attached to. The two balances behave differently at checkout, so
 * the place people already point at is the right place to spell that out.
 *
 * Rendered on hover and on keyboard focus, and hidden from pointer events so it can never sit
 * between the cursor and the button underneath it.
 */
export const FundsBreakdown = () => {
    const { data: balance, isFetched } = useBalance()
    if (!isFetched) return null

    const bonus = balance?.airdrop ?? 0
    const expiry = bonus > 0 && balance?.airdropExpiredAt ? new Date(balance.airdropExpiredAt) : undefined

    return (
        <div className='absolute top-full right-0 pt-3 z-50 hidden md:group-hover/funds:block md:group-focus-within/funds:block pointer-events-none'>
            <div className='w-64 bg-black border border-white/20 shadow-2xl shadow-black p-4 flex flex-col gap-3 text-left font-normal'>
                <div className='text-2xs uppercase tracking-[0.2em] text-white/40'>Your funds</div>

                <div className='flex flex-col gap-1'>
                    <div className='flex flex-row items-center justify-between gap-3'>
                        <SpendableFunds amount={balance?.usdc} className='text-base' />
                        <span className='text-2xs uppercase tracking-wide text-white/50'>Spendable</span>
                    </div>
                    <p className='text-xs text-white/50'>Store purchases, entries and withdrawals.</p>
                </div>

                <div className='border-t border-white/10' />

                <div className='flex flex-col gap-1'>
                    <div className='flex flex-row items-center justify-between gap-3'>
                        <BonusFunds amount={bonus} showLabel={false} className='text-base' />
                        <span className='text-2xs uppercase tracking-wide text-amber-400/70'>Bonus</span>
                    </div>
                    <p className='text-xs text-white/50'>
                        {bonus > 0
                            ? 'In-game spawn fees only. Cannot be spent in the store or withdrawn.'
                            : 'None right now. Bonus funds cover in-game spawn fees only.'}
                    </p>
                    {expiry && (
                        <p className='text-xs text-amber-400/70'>
                            Expires {expiry.toLocaleDateString()} at {expiry.toLocaleTimeString()}
                        </p>
                    )}
                </div>

                <div className='border-t border-white/10' />
                <div className='text-2xs uppercase tracking-wide text-white/40'>Click to deposit or withdraw</div>
            </div>
        </div>
    )
}
